<template>
  <div class="card mb-25 border-0 rounded-0 bg-white add-user-card">
    <div class="card-body p-15">
      <Form ref="questionForm" @submit="addQuestion" :validation-schema="questionSchema">
        <div class="row">
          <!-- Ligne unique : Niveau, Filière, Matière -->
          <div class="col-md-4">
            <div class="form-group mb-10">
              <label class="d-block text-black fw-semibold mb-10">
                Niveau <span class="text-danger">*</span>
              </label>
              <Field name="niveau" v-slot="{ field, handleChange }">
                <Multiselect
                  :options="niveauOptions"
                  :searchable="true"
                  track-by="value"
                  label="label"
                  v-bind="field"
                  @change="handleChange"
                  @select="onNiveauChange"
                  placeholder="Sélectionner un niveau"
                />
              </Field>
              <ErrorMessage name="niveau" class="text-danger" />
            </div>
          </div>

          <div class="col-md-4">
            <div class="form-group mb-10">
              <label class="d-block text-black fw-semibold mb-10">
                Filière
              </label>
              <Field name="filiere" v-slot="{ field, handleChange }">
                <Multiselect
                  :options="filiereOptions"
                  :searchable="true"
                  track-by="value"
                  label="label"
                  v-bind="field"
                  @change="handleChange"
                  @select="onFiliereChange"
                  placeholder="Sélectionner une filière (facultatif)"
                  :disabled="!selectedNiveau || isLoadingFilieres"
                />
              </Field>
              <ErrorMessage name="filiere" class="text-danger" />
            </div>
          </div>

          <div class="col-md-4">
            <div class="form-group mb-10">
              <label class="d-block text-black fw-semibold mb-10">
                Matière <span class="text-danger">*</span>
              </label>
              <Field name="matiere" v-slot="{ field, handleChange }">
                <Multiselect
                  :options="matiereOptions"
                  :searchable="true"
                  track-by="value"
                  label="label"
                  v-bind="field"
                  @change="handleChange"
                  placeholder="Sélectionner une matière"
                  :disabled="!selectedNiveau || isLoadingMatieres"
                />
              </Field>
              <ErrorMessage name="matiere" class="text-danger" />
            </div>
          </div>
        </div>

        <!-- Deuxième ligne : Contenu -->
        <div class="row">
          <div class="col-md-12">
            <div class="form-group mb-10">
              <label class="d-block text-black fw-semibold mb-10">
                Contenu <span class="text-danger">*</span>
              </label>
              <Field
                name="contenu"
                type="text"
                class="form-control shadow-none text-black"
                placeholder="Entrer le contenu"
                v-model="formData.contenu"
              />
              <ErrorMessage name="contenu" class="text-danger" />
            </div>
          </div>
        </div>

        <!-- Section des propositions -->
        <div class="border border-primary mb-10">
          <div class="row d-flex align-items-center justify-content-between fw-bold py-2 bg-primary">
            <div class="col-md-7">
              <h4 class="fs-4 text-white">Ajouter les propositions</h4>
            </div>
            <div class="col-md-5">
              <div class="d-flex float-end">
                <button class="btn btn-success" type="button" @click="addProposition">
                  Ajouter une proposition
                </button>
              </div>
            </div>
          </div>
          <div class="marge-droite">
            <div v-for="(proposition, index) in propositions" :key="index">
              <div class="row mt-10">
                <div class="col-md-4">
                  <label class="d-block text-black fw-semibold mb-10">
                    Contenu <span class="text-danger">*</span>
                  </label>
                </div>
                <div class="col-md-3">
                  <label class="d-block text-black fw-semibold mb-10" style="margin-left: 320px; width: fit-content;">Correcte</label>
                </div>
                <div class="col-md-2">
                  <label class="d-block text-black fw-semibold mb-10" style="margin-left: 240px; width: fit-content;">Action</label>
                </div>
              </div>
              <div class="row">
                <div class="col-md-8">
                  <div class="form-group mb-10">
                    <Field
                      :name="`propositions[${index}].contenu`"
                      type="text"
                      :class="[
                        validateField(proposition.contenu)
                          ? 'form-control shadow-none text-black is-invalid'
                          : 'form-control shadow-none text-black',
                      ]"
                      placeholder="Entrer le contenu de la proposition"
                      v-model="proposition.contenu"
                    />
                    <ErrorMessage :name="`propositions[${index}].contenu`" class="text-danger" />
                  </div>
                </div>
                <div class="col-md-2">
                  <div class="form-group mb-10">
                    <input
                      type="checkbox"
                      class="form-check-input"
                      v-model="proposition.estLaBonneReponse"
                      :true-value="true"
                      :false-value="false"
                    />
                  </div>
                </div>
                <div class="col-md-2">
                  <button
                    class="btn btn-danger transition border-0 pb-2 ps-8 pe-8"
                    type="button"
                    @click="removeProposition(index)"
                    v-if="propositions.length > 1"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" class="text-white">
                      <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                      <path fill-rule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <hr class="mt-0" />
        </div>

        <div class="col-md-12 mt-5">
          <div class="d-flex align-items-center">
            <button
              class="btn btn-success me-3"
              type="submit"
             
              :class="{ 'cursor-not-allowed': isDisable }"
            >
              Ajouter une question
            </button>
            <router-link to="/liste-questions" class="btn btn-danger">
              Annuler
            </router-link>
          </div>
        </div>
      </Form>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, reactive, computed, onMounted } from 'vue';
import { Form, Field, ErrorMessage } from 'vee-validate';
import * as Yup from 'yup';
import ApiService from '@/services/ApiService';
import { success, error } from '@/utils/utils';
import Multiselect from '@vueform/multiselect';
import { useRouter } from 'vue-router';

interface Option {
  value: string | number;
  label: string;
}

export default defineComponent({
  name: 'AddQuestion',
  components: { Form, Field, ErrorMessage, Multiselect },
  setup() {
    const router = useRouter();
    const questionForm = ref<InstanceType<typeof Form> | null>(null);

    // Données du formulaire
    const formData = reactive({
      contenu: '',
      niveau: null as any,
      matiere: null as any,
      filiere: null as any,
    });

    const propositions = reactive([{ contenu: '', estLaBonneReponse: false }]);

    // Schéma de validation
    const questionSchema = Yup.object().shape({
      contenu: Yup.string().required('Le contenu est obligatoire'),
      niveau: Yup.number().nullable().required('Le niveau est obligatoire'),
      matiere: Yup.number().nullable().required('La matière est obligatoire'),
      filiere: Yup.number().nullable().notRequired(),
    });

    // Options pour les champs select
    const niveauOptions = ref<Option[]>([]);
    const matiereOptions = ref<Option[]>([]);
    const filiereOptions = ref<Option[]>([]);

    // Variables de contrôle
    const selectedNiveau = ref<number | null>(null);
    const selectedFiliere = ref<number | null>(null);
    const isLoadingMatieres = ref(false);
    const isLoadingFilieres = ref(false);

    // Validation des champs
    const validateField = (value: string) => !value || value.trim() === '';

    // Désactivation du bouton
    const isDisable = computed(() => {
      const mainValid = formData.contenu && formData.niveau && formData.matiere;
      const propsValid = propositions.every(p => p.contenu?.trim());
      return !(mainValid && propsValid);
    });

    // ========== CHARGEMENT DES DONNÉES ==========

    const loadNiveaux = async () => {
      try {
        const { data } = await ApiService.get('/all/niveaux?limit=0');
        niveauOptions.value = data.data.data.map((n: any) => ({
          value: n.id,
          label: n.libelle,
        }));
      } catch (err: any) {
        error(err.response?.data?.message || 'Erreur chargement niveaux');
      }
    };

    const loadFilieresParNiveau = async (niveauId: number) => {
      if (!niveauId) {
        filiereOptions.value = [];
        return;
      }

      isLoadingFilieres.value = true;
      try {
        const { data } = await ApiService.get(`/filiereNiveauMatieres?niveau=${niveauId}&limit=0`);
        if (data.code === 200 && data.data.data.length > 0) {
          const filieresUniques = Array.from(
            new Set(data.data.data.map((fnm: any) => fnm.filiere.id))
          ).map(id => data.data.data.find((fnm: any) => fnm.filiere.id === id).filiere);

          filiereOptions.value = filieresUniques.map((f: any) => ({
            value: f.id,
            label: f.libelle,
          }));
        } else {
          filiereOptions.value = [];
        }
      } catch (err: any) {
        error('Erreur chargement filières');
        filiereOptions.value = [];
      } finally {
        isLoadingFilieres.value = false;
      }
    };

    const loadMatieresFiltered = async (niveauId: number, filiereId?: number | null) => {
      if (!niveauId) {
        matiereOptions.value = [];
        return;
      }

      isLoadingMatieres.value = true;
      try {
        let url = `/filiereNiveauMatieres?niveau=${niveauId}&limit=0`;
        if (filiereId) url += `&filiere=${filiereId}`;

        const { data } = await ApiService.get(url);
        if (data.code === 200 && data.data.data.length > 0) {
          const matieresUniques = Array.from(
            new Set(data.data.data.map((fnm: any) => fnm.matiere.id))
          ).map(id => data.data.data.find((fnm: any) => fnm.matiere.id === id).matiere);

          matiereOptions.value = matieresUniques.map((m: any) => ({
            value: m.id,
            label: m.libelle,
          }));
        } else {
          matiereOptions.value = [];
        }
      } catch (err: any) {
        error('Erreur chargement matières');
        matiereOptions.value = [];
      } finally {
        isLoadingMatieres.value = false;
      }
    };

    // ========== GESTIONNAIRES ==========

    const onNiveauChange = async (value: any) => {
      const niveauId = typeof value === 'object' ? value?.value : value;
      selectedNiveau.value = niveauId;

      // Réinitialisation
      selectedFiliere.value = null;
      formData.filiere = null;
      formData.matiere = null;
      filiereOptions.value = [];
      matiereOptions.value = [];
      await questionForm.value?.setFieldValue('filiere', null);
      await questionForm.value?.setFieldValue('matiere', null);

      if (niveauId) {
        await loadFilieresParNiveau(niveauId);
        await loadMatieresFiltered(niveauId);
      }
    };

    const onFiliereChange = async (value: any) => {
      const filiereId = typeof value === 'object' ? value?.value : value;
      selectedFiliere.value = filiereId;

      formData.matiere = null;
      matiereOptions.value = [];
      await questionForm.value?.setFieldValue('matiere', null);

      if (selectedNiveau.value) {
        await loadMatieresFiltered(selectedNiveau.value, filiereId);
      }
    };

    // ========== PROPOSITIONS ==========

    const addProposition = () => {
      propositions.push({ contenu: '', estLaBonneReponse: false });
    };

    const removeProposition = (index: number) => {
      if (propositions.length > 1) {
        propositions.splice(index, 1);
      }
    };

    // ========== AJOUT QUESTION ==========

    const addQuestion = async (values: any, { resetForm }: any) => {
      const niveauValue = values.niveau;
      const matiereValue = values.matiere;
      const filiereValue = values.filiere;

      if (!niveauValue || !matiereValue) {
        error('Niveau et matière sont obligatoires.');
        return;
      }

      const questionData = {
        contenu: formData.contenu,
        type: 'choix_multiple',
        niveauId: niveauValue,
        matiereId: matiereValue,
        filiereId: filiereValue || null,
        propositions: propositions
          .filter(p => p.contenu?.trim())
          .map(p => ({ contenu: p.contenu, estLaBonneReponse: p.estLaBonneReponse })),
      };

      console.log('Envoi question:', questionData);

      try {
        const { data } = await ApiService.post('/questions', questionData);
        if (data.code === 201) {
          success(data.message);
          resetForm();
          formData.contenu = '';
          formData.niveau = null;
          formData.matiere = null;
          formData.filiere = null;
          selectedNiveau.value = null;
          selectedFiliere.value = null;
          filiereOptions.value = [];
          matiereOptions.value = [];
          propositions.splice(0, propositions.length, { contenu: '', estLaBonneReponse: false });
          router.push({ name: 'ListeQuestionPage' });
        }
      }

 catch (err: any) {
        error(err.response?.data?.message || 'Erreur création question');
      }
    };

    // ========== INIT ==========

    onMounted(() => {
      loadNiveaux();
    });

    return {
      questionSchema,
      questionForm,
      formData,
      propositions,
      niveauOptions,
      matiereOptions,
      filiereOptions,
      selectedNiveau,
      selectedFiliere,
      isLoadingMatieres,
      isLoadingFilieres,
      onNiveauChange,
      onFiliereChange,
      addProposition,
      removeProposition,
      validateField,
      isDisable,
      addQuestion,
    };
  },
});
</script>

<style scoped>
.cursor-not-allowed { cursor: not-allowed; }
.marge-droite { margin-left: 30px; }
</style>